import { cn } from '../../../lib/stylingUtils';
import { Col, Row } from '../../ui';

type FieldDiff = {
  field: string;
  label: string;
  previous: string | null | undefined;
  current: string | null | undefined;
};

type DiffViewerProps = {
  diffs: FieldDiff[];
  previousLabel?: string;
  currentLabel?: string;
  className?: string;
};

type DiffRowProps = {
  diff: FieldDiff;
  previousLabel: string;
  currentLabel: string;
};

const DiffRow = ({ diff, previousLabel, currentLabel }: DiffRowProps) => {
  const hasChanged = diff.previous !== diff.current;

  if (!hasChanged) {
    return null;
  }

  return (
    <Col className="@DiffRow border-b border-neutral/10 py-2 last:border-b-0">
      <span className="mb-1 text-xs font-medium text-neutral/80">
        {diff.label}
      </span>
      <Row className="gap-4" gap4>
        <Col className="flex-1">
          <span className="text-xs text-neutral/50">{previousLabel}</span>
          <span className="rounded bg-failure/10 px-2 py-1 text-sm text-failure/80">
            {diff.previous ?? 'N/A'}
          </span>
        </Col>
        <Col className="flex-1">
          <span className="text-xs text-neutral/50">{currentLabel}</span>
          <span className="rounded bg-success/10 px-2 py-1 text-sm text-success/80">
            {diff.current ?? 'N/A'}
          </span>
        </Col>
      </Row>
    </Col>
  );
};

export const DiffViewer = ({
  diffs,
  previousLabel = 'Previous',
  currentLabel = 'Current',
  className,
}: DiffViewerProps) => {
  const changedDiffs = diffs.filter((d) => d.previous !== d.current);

  if (changedDiffs.length === 0) {
    return (
      <Col
        className={cn(
          '@DiffViewer',
          'items-center justify-center p-4 text-neutral/40',
          className,
        )}
      >
        <span>No changes detected</span>
      </Col>
    );
  }

  return (
    <Col className={cn('@DiffViewer', 'gap-1', className)}>
      {changedDiffs.map((diff) => (
        <DiffRow
          key={diff.field}
          diff={diff}
          previousLabel={previousLabel}
          currentLabel={currentLabel}
        />
      ))}
    </Col>
  );
};
